import { Component } from "inferno";
import { AppState, Configuration } from "../../services/configuration";
import { Bundle } from '../../services/translationProvider';
import { resizeApp } from '../../utils/util';
import { CloseButton } from "./CloseButton"
import { ChannelEntry } from "./ChannelEntry";
import { ChannelDetail } from "./ChannelDetail"
import { MessageProvider, MessageType, IStyleMessage } from '../../services/messageProvider';
import { Logger } from "../../services/logger";
import { LaunchPadItem, SubChannel } from '../../utils/types';
import { ChannelDefaults } from './ChannelDefaults';
import { EventBus } from "@sap/cpaas-commlib-lib";

const rem = 16; // 1rem = 16px

// ToDo: Button sizes should be themeable, not hardcoded like this:
const closeButtonSize = 54; //px
const channelButtonSize = 47; //px
const buttonSpacing = 0.3; //rem

interface IAppState {
	showChannel: undefined | false | LaunchPadItem,
	iconsLoaded: boolean,
	listenersAdded: boolean,
}

export class Launchpad extends Component<any, any> {

	page: any;
	content: any;
	windowInnerWidth: number;
	windowInnerHeight: number;
	position = Configuration.launchpad.position || 'horizontal';
	channelCount = Configuration.launchpad.channels.length;
	appWidth: number;
	appHeight: number;
	longSideMax: number;
	dynamicallySizedElement: {
		width: number;
		height: number;
		waitBeforeCorrection: any
	}
	resizeTimer: ReturnType<typeof setTimeout>;
	pendingOrientationChange:false|{ orientation: string, clientWidth: number, clientHeight: number, id:string } = false;
	closeButtonIcon: string;
	items: Array<LaunchPadItem> = [];
	lang: string = "EN";
	buttonsOnEdge: boolean = true; // ToDo: This could/should be made configurable
	isIE: boolean;

	constructor(props:any) {
		super(props)
		this.state = {
			showChannel: false,
			iconsLoaded: false,
			listenersAdded: false,
			messagingAppOpening: false,
			launchpadIsOpen: true
		} as IAppState;
	};
	
	componentWillMount() {
		this.isIE = /MSIE|Trident/.test(window.navigator.userAgent);

		// Calculate desired/ideal length of the "long side" of app (e.g. x-axis in horizontal layout)
		const extraWidth = 2*rem; // To account for all paddings and margins, at least 1rem must be added for layoyt to work. But just for visual effect let's add 2rem and "stretch" the content area to be slightly wider than the button row would require
		const multiplier = this.channelCount >= 3 ? this.channelCount : 3; // If less than 3 channels, target same size as with 3 channels.
		this.longSideMax = closeButtonSize + multiplier*channelButtonSize + extraWidth; // The ideal calculated length of the long side (x-axis width in horizontal mode, y-axis height in vertical mode).
		this.initChannels();
	};

	componentDidMount() {
		Logger.trace('Launchpad::componentDidMount. Config: ' + JSON.stringify(Configuration.launchpad,null,'\t'));
		EventBus.subscribe(Configuration.startLaunchpadEvent, this.onLaunchpadStart, this, Configuration.component);
		EventBus.subscribe(Configuration.sizeEvent, this.onAppResize, this, Configuration.component);
		EventBus.subscribe(Configuration.sizeEventFrameNotResized, this.onBrowserResizeButFrameNotResized, this, Configuration.component);
		EventBus.subscribe(Configuration.orientationChange, this.onOrientationChange, this, Configuration.component);
		this.lang = AppState.language.split('-')[0].toUpperCase(); // Configurator provides e.g. "en", while AppState.language could be "en-GB".
		Logger.trace('Launchpad::Launguage: ['+AppState.language+']->['+this.lang+']. IsMobile=['+Configuration.isMobile+']')

		// Launchpad view is produced inside the same 'cvc-frame' iFrame as the original web chat view. 
		// The 'box-shadow' CSS prop is hardcoded for the iFrame. 
		// For backwards compatibility (=not change existing customers' chat appearance), let's not remove the shadow prop from code.
		// Instead remove it in runtime just for Launchpad. Same kind of shadow is applied to Launchpad's background-div in main.css.
		const disableIFrameShadow : IStyleMessage = { targetElementId: 'cvc-frame', style: { "box-shadow": "none" }};
		MessageProvider.sendMessage(MessageType.style, disableIFrameShadow);

		// In Launchpad's case we cannot allow user to resize the view (layout doesn't support resize atm...):
		this.props.allowUserResize(false);

		// Resize app: Need to allow time for DOM to finish after render(), so that we can reliably check the lpWelcome dimensions.
		// Knowing the dimensions allows resizing so that there is good looking amount of space between the (absolutely bottom-positioned) button row, and the upper content row:
		let waitForDOM = setInterval(() => {
			const dynamicallySizedElement = document.getElementById("lpWelcome");
			if (!dynamicallySizedElement) {
				Logger.debug('Launchpad::componentDidMount - Waiting for DOM to finish...');
			}
			else{
				// Elements in DOM. Calculate & request initial ideal size for app.
				clearTimeout(waitForDOM);
				this.setIdealAppSize(Configuration.initialClientWidth, Configuration.initialClientHeight);
			}
		}, 250);
	};

	componentWillUnmount() {
		EventBus.unsubscribe(Configuration.startLaunchpadEvent, this.onLaunchpadStart, this, Configuration.component);
		EventBus.unsubscribe(Configuration.sizeEvent, this.onAppResize, this, Configuration.component);
		EventBus.unsubscribe(Configuration.sizeEventFrameNotResized, this.onBrowserResizeButFrameNotResized, this, Configuration.component);
		EventBus.unsubscribe(Configuration.orientationChange, this.onOrientationChange, this, Configuration.component);
	}

	componentDidUpdate(lastProps:any, lastState:any, snapshot:any) {
		if (!this.state.launchpadIsOpen) {
			Logger.debug('Launchpad::componentDidUpdate - App currently closed, no action');
			return;
		}
		if (this.state.showChannel === undefined || this.state.showChannel !== lastState.showChannel || this.state.messagingAppOpening !== lastState.messagingAppOpening || this.state.launchpadIsOpen !== lastState.launchpadIsOpen) {
			if (this.pendingOrientationChange) {
				// If Launchpad was first opened, then closed, then orientation change occured, and finally user clicks to open Launchpad again.
				Logger.debug('Launchpad::componentDidUpdate - Handle pending orientation change');
				this.setIdealAppSize(this.pendingOrientationChange.clientWidth, this.pendingOrientationChange.clientHeight);
				this.pendingOrientationChange = false;
			} else {
				// Adjust app size.
				const dynamicallySizedElement = this.state.showChannel ? document.getElementById('lpChannelDetails') : document.getElementById("lpWelcome");
				const channelsContainer = document.getElementById('channelsContainer');
				if (!dynamicallySizedElement || !channelsContainer) {
					Logger.warning('Launchpad::componentDidUpdate - Element(s) not in DOM');
					return;
				}
				if (this.position === 'horizontal' && this.dynamicallySizedElement) {
					const dynamicallySizedElementRect = dynamicallySizedElement.getBoundingClientRect();
					const dynamicallySizedElementChangeY = dynamicallySizedElementRect.height - this.dynamicallySizedElement.height;
					this.appHeight += dynamicallySizedElementChangeY;
					Logger.debug('Launchpad::componentDidUpdate - Height adjustment: ['+dynamicallySizedElementChangeY+']px. Resize to ' + this.appWidth + ' / ' + this.appHeight + ' for ['+this.position+'] layout');
					resizeApp(this.appWidth, this.appHeight, true, 'launchpad');
				}
			}
		} else {
			Logger.debug('Launchpad::componentDidUpdate - Size adjustments not needed');
		}

		// When application language is changed on-demand via cvc.setLanguage():
		let appLang = AppState.language.split('-')[0].toUpperCase();
		if (this.lang !== appLang) {
			this.lang = appLang;
			Logger.trace('Launchpad:Language switching to ['+this.lang+']')
			this.forceUpdate();
		}
	};

	onLaunchpadStart = () => {
		Logger.debug('Launchpad::onLaunchpadStart - Launchpad was re-opened');
		this.setState({	launchpadIsOpen: true}); // Set state -> componentDidUpdate
	};

	onOrientationChange = (channel: string, event: string, newOrientation: { orientation: string, clientWidth: number, clientHeight: number, id:string }) => {
		if (Configuration.isMobile) {
			Logger.debug('Launchpad::onOrientationChange - Orientation changed to: ' + JSON.stringify(newOrientation));
			if (!this.state.launchpadIsOpen) {
				Logger.debug('Launchpad::onOrientationChange - Orientation changed while app was closed. Set as pending orientation change');
				this.pendingOrientationChange = newOrientation;
			} else {
				Logger.debug('Launchpad::onOrientationChange - Orientation changed while app was open, set ideal app size for ['+newOrientation.orientation+'] orientation');
				this.setIdealAppSize(newOrientation.clientWidth, newOrientation.clientHeight);
			}
		}
	}

	onBrowserResizeButFrameNotResized = (channel: string, event: string, newSize: { clientWidth: number, clientHeight: number, id:string }) => {
		if (!Configuration.isMobile) {
			Logger.debug('Launchpad::onBrowserResizeButFrameNotResized - Checking if app size can be increased for ['+this.position+'] layout');
			if (this.position === 'horizontal') {
				if (newSize.clientWidth > this.appWidth && newSize.clientWidth <= this.longSideMax) {
					if (this.resizeTimer) {
						clearTimeout(this.resizeTimer);
					}
					this.resizeTimer = setTimeout(() => {
						// On desktop, when browser is resized there can be a surge of resize events. React only to last one within 250ms.
						Logger.debug('Launchpad::onBrowserResizeButFrameNotResized - Available browser width ['+newSize.clientWidth+'], current frame width ['+this.appWidth+']. Increasing width for ['+this.position+'] layout');
						this.setIdealAppSize(newSize.clientWidth, newSize.clientHeight);
					},50);
				}
			}
		}
	};

	onAppResize = (channel: string, event: string, newSize: { x: number, y: number, clientWidth: number, clientHeight: number, id:string }) => {
		Logger.debug('Launchpad::onAppResize - App resized to: ' + JSON.stringify(newSize));
		// React to app having been resized due to request from Launchpad code.
		if (newSize.id === 'launchpad') {
			// After app resize, store dimensions of dynamically sized element (welcome text, or channel details).
			// This allows x and y changes to be calculated when user selects another channel, and Launchpad can request app size change accordingly.
			const dynamicallySizedElement = this.state.showChannel ? document.getElementById('lpChannelDetails') : document.getElementById("lpWelcome");
			const channelsContainer = document.getElementById('channelsContainer');
			const layoutContainer = document.getElementById('layoutContainer');
			if (!dynamicallySizedElement || !channelsContainer || !layoutContainer) {
				Logger.error('Launchpad::onAppResize - Elements not found in DOM after resize.'); //Should never happen
				return;
			}
			const dynamicallySizedElementRect = dynamicallySizedElement.getBoundingClientRect();
			const channelsContainerRect = channelsContainer.getBoundingClientRect();

			this.dynamicallySizedElement = {
				width: dynamicallySizedElementRect.width,
				height: dynamicallySizedElementRect.height,
				waitBeforeCorrection: null
			}

			// Store new size.
			this.appWidth = newSize.x;
			this.appHeight = newSize.y;
			Logger.debug('Launchpad::onAppResize - App was resized to ' + this.appWidth + ' / ' + this.appHeight + ' for ['+this.position+'] layout');

			// In horizontal layout, if client width restricted app width, apply adjusted/increased height.
			if (this.position === 'horizontal') {
				const overlapY = Math.ceil(newSize.y - dynamicallySizedElementRect.height - (channelsContainerRect.height + 2*rem)) * -1;
				const increasedHeight = this.appHeight += overlapY;
				if (overlapY > 0 && increasedHeight <= newSize.clientHeight) {
					this.appHeight = increasedHeight;
					Logger.debug('Launchpad::onAppResize - Increase height by ['+overlapY+'] to ['+this.appHeight+'] for ['+this.position+'] layout');
					resizeApp(this.appWidth, this.appHeight, true, 'launchpad');
				} else if (increasedHeight > newSize.clientHeight) {
					// This would only happen on very small screens, and if a lot of text content, and maybe if QR-code image is defined for messaging channel.
					Logger.debug('Launchpad::onAppResize - Too much content. Cannot further increase height by ['+overlapY+'] to get to ideal height. Keeping current height ['+this.appHeight+'] for ['+this.position+'] layout');
				}
			}
		// React to app resized due to "external" factor: Browser resize on desktop, orientation change on mobile, or due to a cvc.resizeContainer call.
		} else if (this.state.launchpadIsOpen) {
			if (this.resizeTimer) {
				clearTimeout(this.resizeTimer);
			}
			this.resizeTimer = setTimeout(() => {
				// On desktop, when browser is resized there can be a surge of resize events. React only to last one within 250ms.
				this.setIdealAppSize(newSize.clientWidth, newSize.clientHeight);
			},50);
		}
	};

	setIdealAppSize = (clientWidth:number, clientHeight:number) => {
		if (!this.state.launchpadIsOpen) {
			Logger.debug('Launchpad::setIdealAppSize - Launchpad currently closed, skip changing size');
			return;
		}
		const channelsContainer = document.getElementById('channelsContainer');
		const dynamicallySizedElement = this.state.showChannel ? document.getElementById('lpChannelDetails') : document.getElementById("lpWelcome");
		if (!channelsContainer || !dynamicallySizedElement) {
			Logger.warning('Launchpad::setIdealAppSize - Element(s) not in DOM, aborting.');
			return;
		}
		const dynamicallySizedElementRect = dynamicallySizedElement.getBoundingClientRect();
		const channelsContainerRect = channelsContainer.getBoundingClientRect();
		if (this.position === "horizontal") {
			// Determine ideal values:
			this.appWidth = this.longSideMax;
			this.appHeight = Math.ceil(dynamicallySizedElementRect.height + channelsContainerRect.height + 2*rem);
			// However if ideal values exceed browser limits, there is no point for asking for more:
			if (this.appWidth > clientWidth) {
				this.appWidth = clientWidth;
			}
			if (this.appHeight > clientHeight) {
				this.appHeight = clientHeight;
			}
			Logger.debug('Launchpad::setIdealAppSize - Resize to ' + this.appWidth + ' / ' + this.appHeight + ' for ['+this.position+'] layout');
			resizeApp(this.appWidth, this.appHeight, true, 'launchpad');
		} else {
			Logger.warning('Launchpad::setIdealAppSize - Layout of type ['+this.position+'] is not supported, aborting.');
		}		
	}

	initChannels = async () => {
		for (var channel of Configuration.launchpad.channels) {	
			Logger.trace('Launchpad::Loading ['+channel.type+'] with provided config: ' + JSON.stringify(channel));
			channel = { ...ChannelDefaults[channel.type], ...channel };
			
			// Handle channel icon or image. ChannelEntry and ChannelDetail check which one is used, and show it accordingly.
			if (typeof channel.icon === "string" && channel.icon.endsWith(".svg")) {
				Logger.trace('Launchpad::Loading [' + channel.type + '] using standard icon [' + channel.icon + ']');
				try {
					channel.icon = await (await fetch("resources/ico/fixed/" + channel.icon)).text();
				} catch (e) {
					//ignore
					Logger.error('Launchpad::Problem when loading icon for ' + channel.type);
				}
			} else if (typeof channel.icon === "string" && channel.icon.startsWith("<svg")) {
				Logger.trace('Launchpad::Loading [' + channel.type + '] with custom SVG element as icon');
			} else if (typeof channel.icon === "object" && channel.icon.name && channel.icon.base64) {
				Logger.trace('Launchpad::Loading [' + channel.type + '] with custom image [ ' + channel.icon.name + ']');
			} else {
				Logger.error('Launchpad::No icon or image defined for Channel ['+channel.type+'], skip showing this channel');
				continue;
			}

			if (!channel.address || channel.address.length === 0) {
				channel.address = "addr-not-configured"; // This will be shown in Launchpad, and should alert admin to fix configuration.
				if (channel.type !== "webchat") { // WebChat is the only one without address (it has array of queues instead)
					Logger.error('Launchpad::Channel ['+channel.type+'] has no configured address');
				}
			}

			// Convert channel address to "proper" technical address, for applicable channels:
			switch (channel.type) {
				case SubChannel.messenger:
					channel.properAddress = channel.address; //This could be almost anything. Alphanumeric probably. Just set it.
					break;
				case SubChannel.email:
					break;
				case SubChannel.webchat:
					break;
				case SubChannel.callback:
					break;
				case SubChannel.viber:
					break;
				case SubChannel.viberChatbot:
					break;
				case SubChannel.viberBusinessMessages:
					break;
				case SubChannel.custom:
					break;
				default: // Channels that use a phone number as address:

					// Configurator allows few special characters in phone number, mainly for US and UK. For example this is a valid number: (001) 555-123456 
					channel.properAddress = channel.address.replace(/\-| |\(|\)|^0/g, ""); //Remove special chars and store a proper number

					// Ensure phone numbers start with either + or 00:
					if (!channel.properAddress.startsWith('+') && !channel.properAddress.startsWith('00')) {
						Logger.error('Launchpad::Channel ['+channel.type+'] incorrect number format.');
						continue; //Skip to next channel
					}

					// But normalize all numbers to start with +:
					if (channel.properAddress.startsWith('00')) {
						channel.properAddress = "+" + channel.properAddress.substr(2);
					}
			}

			// Setup this channel with urls. Usually there is only one, but at least for Whatsapp there is a backup URL also:
			if (channel.type !== SubChannel.webchat) this.setUrls(channel);

			this.items.push(channel);
		}
		this.closeButtonIcon = await (await fetch("resources/ico/themeable/buttons/Close.svg")).text();
		this.state.iconsLoaded = true;
		this.forceUpdate();
	};


	showChannelDetails = (item: LaunchPadItem) => {
		Logger.trace('Launchpad::Switching to ' + item.title + ' details');
		this.setState({	showChannel: item, messagingAppOpening: false});
	};

	onMessagingAppOpenClick = () => {
		this.setState({	messagingAppOpening: true});
	};

	onCloseButtonClick = () => {
		Logger.debug('Launchpad::onCloseButtonClick');
		this.state.launchpadIsOpen = false; // Write value directly, no need to cause render as app isn't visible
		this.state.showChannel = false; // -> show welcome screen next time app if app is opened again
	};

	startWebChat = () => {
		Logger.trace('Launchpad::Launching Web Chat');
		const reEnableIFrameShadow : IStyleMessage = { targetElementId: 'cvc-frame', style: { "box-shadow": "0px 0px 10px 1px rgba(0,0,0,0.3)" }};
		MessageProvider.sendMessage(MessageType.style, reEnableIFrameShadow);
		this.props.allowUserResize(true);
		setTimeout(() => {
			MessageProvider.sendMessage(MessageType.upgradeToWebChat);
		}, 100);
	};

	setUrls(item:LaunchPadItem) {
		let dest = item.properAddress || item.address;
		switch (item.type) {
			case SubChannel.whatsapp:
				item.altUrl = "https://api.whatsapp.com/send?phone=" + dest; // May 2021 testing: Using this https url works always with both desktop and mobile. However, using the whatsapp:// protocol is more straightforward. Currently not using the altUrl for anything.
				item.url = "whatsapp://send?phone=" + dest.substring(1); // May 2021 testing: Whatsapp never wants the leading +
				return true;
			case SubChannel.viber: // https://tickets.sinch.com/browse/CC365V1-7330
			case SubChannel.viberChatbot:
				item.url = "viber://pa?chatURI=" + dest; // https://tickets.sinch.com/browse/CC365V1-7179
				return true;
			case SubChannel.viberBusinessMessages:
				item.url = "viber://chat?service=" + dest; // https://tickets.sinch.com/browse/CC365V1-7330
				return true;
			case SubChannel.sms:
				item.url = "sms://" + dest;
				return true;
			case SubChannel.email:
				item.url = "mailto:" + dest;
				return true;
			case SubChannel.phone:
				item.url = "tel:" + dest;
				return true;	
			case SubChannel.messenger:
				item.url = "https://m.me/" + dest; // As of May 2021, deep-linking is not possible with Messenger. Must open https url to start a chat.
				return true;
			case SubChannel.custom:
				item.url = item.address; // For custom channels the whole URL or deep-link is defined in Configurator
				return true;
			default:
				Logger.error('Launchpad::Cannot create link for ['+item.type+']');
				return false;
		}
	};

	render() {
		
		if(!this.items || this.items.length === 0) {
			Logger.debug('Launchpad::Waiting for configuration...');
			return;
		}

		const Welcome = () => {
			const spanAlign = Configuration.launchpad.mirrorUi ? "justify-content:flex-end;" : "justify-content:flex-start;";
			const pAlign = this.lang === "AR" ? "text-align: end;" : "text-align: start;";
			const welcomeText = Configuration.launchpad.headers && Configuration.launchpad.headers[this.lang] ? Configuration.launchpad.headers[this.lang] : Bundle.t("VCC_XMSG_LAUNCHPAD_DESC");
			const split = welcomeText.split('|');
			let parts = new Array();
			if (split.length > 1) {
				split.forEach(part => {
					parts.push(part.trimLeft().trimRight());
				});
				parts = [parts.shift(), parts.join('|')];
			}
			else parts.push(split[0]);

			const css = {
				width: "100%"
			};

			return (
				<div id="lpWelcome" className="p-2" style={css}>
					{ parts.length === 1 ? <div className="d-flex flex-row"><span style={spanAlign} className="font-weight-bold" id="lpWelcomeText">{parts[0]}</span></div>:null }
					{ parts.length === 2 ? <>
							<div className="d-flex flex-row"><span style={spanAlign} className="font-weight-bold">{parts[0]}</span></div>
							<div className="d-flex flex-row"><p style={pAlign} id="lpWelcomeText">{parts[1]}</p></div>
						</>:null 
					}
				</div>
			)
		};

		const HorizontalLayout = (props:any) => {

			let backgroundCss = "left:0rem; right:0rem; top:0rem; border-radius:5px 5px 5px 5px; z-index:-1; position: absolute;";
			if (this.buttonsOnEdge)	backgroundCss += "bottom:2.5rem";
			else backgroundCss += "bottom:0rem";
			
			const layoutCss = {
				"width": "100%"
			}

			let channelsCss = "padding-left:0.7rem; padding-right:0.7rem;";
			channelsCss += "position:absolute; bottom:0.5rem;";
			if (this.isIE) channelsCss += "display: -ms-flexbox !important; text-align: center !important;	-ms-flex-align: center !important;";			

			return (<div className="p-2" style={layoutCss}>
				{/* Top row acts as container for welcome message and channel details */}
				<div className="d-flex flex-row" style={this.lang === "AR" ? "justify-content: flex-end": undefined}>
					{ this.state.showChannel 
						? <ChannelDetail item={this.state.showChannel}  lang={this.lang} startWebChat={this.startWebChat} onAppOpenClick={this.onMessagingAppOpenClick} appOpening={this.state.messagingAppOpening}/>
						: <Welcome /> }
				</div>

				{/* Bottom row acts as container for close button and channel specific buttons */}
				<div id="channelsContainer" className={Configuration.launchpad.mirrorUi ? "d-flex flex-row lpScrollX" : "d-flex flex-row-reverse lpScrollX"} style={channelsCss}>
					<CloseButton icon={this.closeButtonIcon} onCloseButtonClick={this.onCloseButtonClick} size={closeButtonSize} spacing={buttonSpacing}/>
					{props.children}
				</div>
				<div className="lpBackground" style={backgroundCss}></div>
			</div>)
		}

		return (
			this.state.iconsLoaded ?  <div className="h-100 w-100 d-flex flex-row lpText" id="layoutContainer" ref={(node: HTMLElement) => this.page = node}> 
				{Configuration.launchpad.position === "vertical" //ToDo: For now, position/layout is always "horizontal". Add more layouts later.
					? <HorizontalLayout>
						{this.items.map((item) => <ChannelEntry item={item} showChannelDetails={this.showChannelDetails} spacing={buttonSpacing}/>)}
					</HorizontalLayout>
					:< HorizontalLayout>
						{this.items.map((item) => <ChannelEntry item={item} showChannelDetails={this.showChannelDetails} spacing={buttonSpacing}/>)}
					</HorizontalLayout>
				}
			</div>:null
		);
	};
}